import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function WalletTab() {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get('/dashboard/profile').then(r => setProfile(r.data)).catch(() => {});
    api.get('/dashboard/wallet')
      .then(r => setWallet(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isPremium = profile?.tier === 'premium' || profile?.tier === 'turbo';
  if (!isPremium) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="wallet-locked">
        <Wallet size={32} className="text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600 font-medium">Wallet is a Premium feature</p>
        <p className="text-xs text-slate-500 mt-1">Upgrade to Premium to unlock AI procedure descriptions, case studies, encrypted data export, and more.</p>
        <Link to="/upgrade" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors" data-testid="wallet-upgrade-btn">
          <Crown size={14} /> Upgrade to Premium
        </Link>
      </div>
    );
  }

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="wallet-tab">
      <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Wallet & Appointment Code</h3>

      {/* Top row — Appointment Code + Wallet Balance tile side-by-side on
          laptops, stack on mobile. Both are equal-rank summary cards. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="wallet-top-grid">
      {/* Booking Code */}
      <div className="bg-gradient-to-r from-sky-50 to-sky-100/50 rounded-2xl border border-sky-200 p-6" data-testid="booking-code-card">
        <p className="text-xs font-medium text-sky-600 uppercase tracking-wider mb-2">Your Appointment Code</p>
        <div className="flex items-center gap-4">
          <span className="text-3xl font-bold text-sky-900 tracking-[0.15em] font-mono" style={{ fontFamily: 'Outfit, monospace' }} data-testid="booking-code">
            {wallet?.booking_code || '------'}
          </span>
          <div className="text-xs text-sky-700 leading-relaxed">
            <p>Your unique appointment code.</p>
            <p>SMS/IVR booking coming soon.</p>
          </div>
        </div>
      </div>

      {/* Wallet — Coming Soon */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="wallet-balance-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Wallet Balance</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Rs. {wallet?.balance || 0}
            </p>
          </div>
          <Wallet size={32} className="text-sky-200" />
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <p className="text-xs font-medium text-amber-700">IVR/SMS Booking — Coming Soon</p>
          <p className="text-[10px] text-amber-600 mt-1">
            Wallet top-up will be enabled once IVR/SMS appointment booking goes live.
            Rs. 3-5 per confirmed appointment. Refunded for no-shows.
          </p>
        </div>
      </div>
      </div>

      {/* Encrypted Data Export */}
      <DataExportSection />
    </div>
  );
}

function DataExportSection() {
  const [password, setPassword] = useState('');
  const [showExportPw, setShowExportPw] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleExport = async () => {
    if (!password || password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setExporting(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/dashboard/export-data', { password });
      setResult(data);
      setPassword('');
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="data-export-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
          <Download size={18} className="text-violet-600" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Encrypted Data Export</h4>
          <p className="text-xs text-slate-500">Download all your interaction data — encrypted and secure</p>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
        Export all your interaction data — appointments, procedures, and profile data as a Fernet-encrypted (AES-128) file sealed with your passphrase.
        Securely stored on the servers, always accessible to you. You'll need your password to decrypt it.
      </p>
      <div className="flex gap-2 items-end">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Set Export Password</label>
          <div className="relative">
            <input type={showExportPw ? 'text' : 'password'} className="rounded-xl border border-slate-200 px-3 py-2 pr-10 text-sm outline-none w-48"
              value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" data-testid="export-password" />
            <button type="button" onClick={() => setShowExportPw(!showExportPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1}>
              {showExportPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="rounded-full px-5 py-2 bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="export-button">
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
          {exporting ? 'Encrypting...' : 'Export & Encrypt'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {result && (
        <div className="mt-4 bg-emerald-50 rounded-xl border border-emerald-200 p-4" data-testid="export-result">
          <p className="text-sm font-medium text-emerald-800 mb-2">Data exported successfully!</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-emerald-700 mb-3">
            <span>Appointments: {result.records.appointments}</span>
            <span>Procedures: {result.records.procedures}</span>
            <span>Locations: {result.records.locations}</span>
            <span>Feedbacks: {result.records.feedbacks}</span>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}${result.download_url}`} download={result.filename}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
            data-testid="download-export">
            <Download size={12} /> Download Encrypted File ({result.size_kb} KB)
          </a>
          <p className="text-[10px] text-emerald-600 mt-2">Keep your password safe — it's needed to decrypt.</p>
        </div>
      )}
    </div>
  );
}

